<script setup lang="ts">

import {
  CurrencyDisplay,
  formatting,
  Models,
  useDialogs,
  useLocalization,
  useOrderViewContext
} from "@hantera/portal-app";
import {DecimalInput} from "@hantera/design-system";
import {computed, ref, watch} from "vue";
import Decimal from "decimal.js";
import StockDialog from "@/components/Order/Deliveries/OrderLine/StockDialog.vue";

const props = defineProps<{
  orderLine: Models.Graph.OrderLine
  delivery: Models.Graph.Delivery
}>();

const t = useLocalization('components.order.orderLine.details');
const orderViewContext = useOrderViewContext();
const dialogs = useDialogs()

const quantityInput = ref(props.orderLine.quantity);

watch(() => props.orderLine.quantity, () => {
  quantityInput.value = props.orderLine.quantity
});

const discounted = computed(() => {
  if (props.orderLine.calculatedDiscounts.length === 0) return new Decimal(0);
  return Decimal.sum(...props.orderLine.calculatedDiscounts.map(r => r.value)).neg();
});

const orderLineTotal = computed(() => {
    return props.orderLine.unitPrice.mul(props.orderLine.quantity).add(discounted.value);
});

async function updateQuantity() {
  if (!props.orderLine.quantity.eq(quantityInput.value)) {
    await orderViewContext.pushPendingChange(
      'setOrderLineQuantity_' + props.orderLine.orderLineId,
      [{
        type: 'setOrderLineQuantity',
        orderLineId: props.orderLine.orderLineId,
        quantity: quantityInput.value,
      }],
      'Change quantity from {previousQuantity} to {newQuantity} of {productNumber} of order line {orderLineNumber} on {deliveryNumber}',
      {
        previousQuantity: props.orderLine.quantity,
        newQuantity: quantityInput.value,
        productNumber: props.orderLine.productNumber,
        orderLineNumber: props.orderLine.orderLineNumber,
        deliveryNumber: props.delivery.deliveryNumber
      }
    )
  }

}

const hasBackOrder = computed(() => {
  return props.orderLine.backOrders.length > 0
})

function openStock(orderLine: Models.Graph.OrderLine) {
  dialogs.openModal(
    'Reservation Details',
    StockDialog,
    true,
    {
      orderLine
    }
  )
}

</script>

<template>
  <div class="detail">
    <div class="detail-label detail-unit-price">{{ t('unitPrice') }}</div>
    <div class="detail-value detail-unit-price">
      {{ formatting.currency(props.orderLine.unitPrice, orderViewContext.state.currencyCode, CurrencyDisplay.none) }}
    </div>
  </div>
  <div class="detail">
    <div class="detail-label detail-quantity">{{ t('quantityWithUnit', { unit: 'pcs' }) }}</div>
    <div class="detail-value detail-quantity">
      <DecimalInput
        :allow-percentage="false"
        v-model="quantityInput"
        @blur="updateQuantity"
      />
    </div>
  </div>
  <div class="detail">
    <div class="detail-label detail-reserved">{{ t('reserved') }}</div>
    <button
      class="btn-link detail-value detail-reserved"
      :class="{
        'detail-value--warning': hasBackOrder && orderLine.reservedQuantity.lt(orderLine.quantity),
        'detail-value--success': orderLine.reservedQuantity.gte(orderLine.quantity) 
      }"
      @click="openStock(orderLine)"
    >{{formatting.number(orderLine.reservedQuantity)}}</button>
  </div>
<!--  <div class="detail">-->
<!--    <div class="detail-label detail-in-stock">{{ t('inStock') }}</div>-->
<!--    <div class="detail-value detail-in-stock">0</div>-->
<!--  </div>-->
  <div class="detail">
    <div class="detail-label detail-discount">{{ t('discount') }}</div>
    <div class="detail-value detail-discount">
      {{ formatting.currency(discounted, orderViewContext.state.currencyCode, CurrencyDisplay.none) }}
    </div>
  </div>
  <div class="detail" style="grid-column: calc(var(--grid-column-offset) - 3)">
    <div class="detail-label detail-sum">{{ t('sum') }}</div>
    <div class="detail-value detail-sum">
      {{ formatting.currency(orderLineTotal, orderViewContext.state.currencyCode, CurrencyDisplay.none) }}
    </div>
  </div>
</template>

<style scoped>

</style>